import type { Comp, GameObj, PosComp } from "kaplay";
import { hazard, type HazardSpec } from "../components/hazard";
import { look } from "../components/look";
import type { ObstacleComp } from "../components/obstacle";
import { config } from "../config";
import type { LookKey } from "../config/assetManifest";
import { PX_PER_M } from "../config/constants";
import type { EntityDescriptor, HazardBase, Species } from "../config/types";
import { k } from "../core/kaplay";
import { registerEntity } from "../core/registry";
import { hash, mulberry32 } from "../core/rng";
import { createDog, hitDog, stepDog } from "../systems/ai/dog";
import { createHiker, hitHiker, stepHiker } from "../systems/ai/hiker";
import { crashNpcRider, createNpcRider, stepNpcRider, type Blocker } from "../systems/ai/npcRider";
import type { Agent } from "../systems/ai/types";
import { canHitAnimal, createAnimal, stepAnimal } from "../systems/ai/wildlife";
import type { Mountain } from "../systems/mountain";

const { dog, hiker, npc_rider: npcRider, wildlife } = config.hazards;
const NPC_HIT_M = 0.2;
const DOG_SIDE_M = [3, 6];
const HIKER_ON_TRAIL_CHANCE = 0.5;

const rngAt = (d: EntityDescriptor, salt: number) => mulberry32(hash(Math.round(d.x), Math.round(d.y), salt));

function besideTrail(mountain: Mountain, yM: number, xM: number, rng: () => number): number {
    const trail = mountain.trailAt(yM);
    if (!trail) return xM;
    const side = rng() < 0.5 ? -1 : 1;
    const gap = DOG_SIDE_M[0] + rng() * (DOG_SIDE_M[1] - DOG_SIDE_M[0]);
    return trail.x + side * (trail.halfWidth + gap);
}

function hitbox(cfg: HazardBase) {
    const [w, h] = cfg.hitboxPx;
    return new k.Rect(k.vec2(-w / 2, -h), w, h);
}

function blockersAhead(x: number, y: number, rangeM: number): Blocker[] {
    return (k.get("obstacle") as GameObj<ObstacleComp | PosComp>[])
        .filter((o) => o.obstacleConfig.kind === "solid")
        .map((o) => ({ x: o.pos.x / PX_PER_M, y: o.pos.y / PX_PER_M, halfWidth: (o.obstacleConfig.hitboxPx?.[0] ?? 32) / 2 / PX_PER_M }))
        .filter((b) => b.y > y - 1 && b.y < y + rangeM && Math.abs(b.x - x) < 10);
}

function addHazard<S extends Agent>(d: EntityDescriptor, key: LookKey, spec: HazardSpec<S>, agent: S, tags: string[]) {
    return k.add([
        k.pos(d.x, d.y),
        look(key, agent.pose),
        k.area({ shape: hitbox(spec.config), isSensor: true }),
        k.z(d.y),
        k.opacity(1),
        hazard(spec, agent, d.variant ?? key) as Comp,
        "hazard",
        "roamer",
        "world",
        ...tags,
    ]);
}

export function registerHazards(mountain: Mountain) {
    registerEntity("dog", (d) => {
        const y = d.y / PX_PER_M;
        const x = besideTrail(mountain, y, d.x / PX_PER_M, rngAt(d, 0x646f67));
        return addHazard(d, "dog", {
            config: dog,
            step: (a, rider, dt) => stepDog(a, rider, dt, dog),
            onHit: (a) => hitDog(a, dog),
        }, createDog(x, y), ["dog"]);
    });

    registerEntity("hiker", (d) => {
        const y = d.y / PX_PER_M;
        const trail = mountain.trailAt(y);
        const onTrail = !!trail && rngAt(d, 0x68696b)() < HIKER_ON_TRAIL_CHANCE;
        const x = onTrail ? trail.x : d.x / PX_PER_M;
        return addHazard(d, "hiker", {
            config: hiker,
            step: (a, rider, dt) => stepHiker(a, rider, mountain.trailAt(a.y), dt, hiker),
            onHit: (a, rider) => hitHiker(a, rider, hiker),
        }, createHiker(x, y, onTrail), ["npc"]);
    });

    registerEntity("npc_rider", (d) =>
        addHazard(d, "npc_rider", {
            config: npcRider,
            step: (a, _rider, dt) => {
                const blockers = blockersAhead(a.x, a.y, npcRider.lookAheadM);
                const next = stepNpcRider(a, mountain.trailAt(a.y)?.x ?? a.x, blockers, dt, npcRider);
                const hitTree = blockers.some((b) => Math.abs(b.y - next.y) < NPC_HIT_M && Math.abs(b.x - next.x) < b.halfWidth);
                return hitTree && next.fallen === 0 ? crashNpcRider(next, npcRider) : next;
            },
            onHit: (a) => crashNpcRider(a, npcRider),
        }, createNpcRider(d.x / PX_PER_M, d.y / PX_PER_M), ["npc"]),
    );

    registerEntity("wildlife", (d) => {
        const species = (d.variant ?? "cow") as Species;
        const rng = rngAt(d, 0x616e);
        return addHazard(d, "wildlife", {
            config: wildlife[species],
            step: (a, rider, dt) => stepAnimal(a, rider, dt, wildlife[species], rng),
            onHit: (a) => a,
            canHit: canHitAnimal,
        }, createAnimal(species, d.x / PX_PER_M, d.y / PX_PER_M), ["npc"]);
    });
}
